import React, { useState, useEffect, useCallback } from 'react';
import { Toaster, toast } from 'sonner';
import { Plus, Pencil, Trash2, X, CheckCircle2, AlertCircle, FileText, Copy, Eye, ShieldCheck } from 'lucide-react';
import {
  listRfqCampaigns, deleteRfqCampaign,
  listRfqEmailLogs,
  copyRfqCampaign, previewRfq,
} from '../../lib/rfqApi';
import { RfqPreSendPanel } from '../../components/admin/RfqPreSendPanel';
import { RfqWizard } from '../../components/admin/RfqWizard';

const CAMP_STATUS_COLOR = {
  draft: 'bg-white/10 text-white/70',
  scheduled: 'bg-amber-400/15 text-amber-200',
  sending: 'bg-cyan-400/15 text-cyan-200',
  sent: 'bg-emerald-400/15 text-emerald-200',
  receiving_responses: 'bg-emerald-400/15 text-emerald-200',
  completed: 'bg-white/5 text-white/40',
  cancelled: 'bg-red-400/15 text-red-200',
  closed: 'bg-white/5 text-white/40',
};

export const AdminRfqCampaigns = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  // Send modal + logs viewer
  const [sendingId] = useState(null);
  const [sendResult, setSendResult] = useState(null);
  const [logsModal, setLogsModal] = useState(null);
  const [logs, setLogs] = useState([]);
  const [previewData, setPreviewData] = useState(null);
  const [preSendCampaign, setPreSendCampaign] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await listRfqCampaigns();
      setItems(data.items || []);
    } catch (e) { toast.error('Failed to load campaigns'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { fetchData(); }, [fetchData]);

  const openCreate = () => { setEditing(null); setWizardOpen(true); };
  const openEdit = (c) => { setEditing(c); setWizardOpen(true); };

  const handleDelete = async (c) => {
    if (!window.confirm(`Delete campaign "${c.campaign_name}"?`)) return;
    try { await deleteRfqCampaign(c.id); toast.success('Deleted'); fetchData(); }
    catch (e) { toast.error('Delete failed'); }
  };

  const handleCopy = async (c) => {
    try {
      await copyRfqCampaign(c.id);
      toast.success('Campaign copied');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Copy failed');
    }
  };

  const handleSend = (c) => {
    if (!c.supplier_ids?.length) {
      toast.error('No suppliers selected — edit the campaign first.');
      return;
    }
    setPreSendCampaign(c);
  };

  const openLogs = async (c) => {
    setLogsModal(c);
    try {
      const { data } = await listRfqEmailLogs({ campaign_id: c.id });
      setLogs(data.items || []);
    } catch (e) { setLogs([]); }
  };

  const handlePreviewCampaign = async (c) => {
    try {
      const { data } = await previewRfq({
        email_subject: c.email_subject,
        email_body: c.email_body,
        product_request_text: c.product_request_text,
        products: c.products,
        destination_country: c.destination_country,
        delivery_location: c.delivery_location,
        target_total_quantity: c.target_total_quantity,
        target_budget: c.target_budget,
        shipping_term: c.shipping_term,
        response_due_date: c.response_due_date,
        warranty: c.warranty,
        condition: c.condition,
        additional_requirements: c.additional_requirements,
      });
      setPreviewData({ ...data, campaign_name: c.campaign_name });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Preview failed');
    }
  };

  return (
    <div className="px-6 py-4 text-white" data-testid="admin-rfq-campaigns">
      <Toaster theme="dark" />
      <div className="flex flex-col gap-1 mb-6">
        <h1 className="text-2xl lg:text-3xl font-bold tracking-tight">RFQ Campaigns</h1>
        <p className="text-white/50 text-sm">Send purchasing requests to selected suppliers.</p>
      </div>

      <div className="mb-5">
        <button data-testid="rfq-create-btn" onClick={openCreate} className="flex items-center gap-2 px-4 py-2.5 bg-[#00D4FF] text-black font-semibold text-sm hover:bg-[#00D4FF]/90">
          <Plus className="w-4 h-4" /> New RFQ Campaign
        </button>
      </div>

      <div className="border border-white/10 overflow-x-auto" data-testid="rfq-table">
        <table className="w-full text-sm">
          <thead className="sticky top-0 z-10 bg-[#0A0A0A] text-white/60 text-xs uppercase tracking-wider shadow-[0_1px_0_rgba(255,255,255,0.05)]">
            <tr>
              <th className="text-left px-4 py-3">Name</th>
              <th className="text-left px-4 py-3">Destination</th>
              <th className="text-left px-4 py-3">Suppliers</th>
              <th className="text-left px-4 py-3">Target Qty</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-left px-4 py-3">Sent At</th>
              <th className="text-right px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan="7" className="px-4 py-10 text-center text-white/40">Loading…</td></tr>}
            {!loading && items.length === 0 && (
              <tr><td colSpan="7" className="px-4 py-10 text-center text-white/40">No RFQ campaigns yet.</td></tr>
            )}
            {!loading && items.map((c) => (
              <tr key={c.id} className="border-t border-white/5 hover:bg-white/[0.02]" data-testid={`rfq-row-${c.id}`}>
                <td className="px-4 py-3 font-medium">{c.campaign_name}</td>
                <td className="px-4 py-3 text-white/60">{c.destination_country || '—'}</td>
                <td className="px-4 py-3 text-white/60">{(c.supplier_ids || []).length}</td>
                <td className="px-4 py-3 text-white/60">{c.target_total_quantity ?? '—'}</td>
                <td className="px-4 py-3"><span className={`inline-block px-2 py-1 text-xs ${CAMP_STATUS_COLOR[c.status] || ''}`}>{c.status}</span></td>
                <td className="px-4 py-3 text-white/40 text-xs">{c.sent_at ? new Date(c.sent_at).toLocaleString() : '—'}</td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <button onClick={() => openLogs(c)} title="View email logs" data-testid={`rfq-logs-${c.id}`} className="p-1.5 text-white/60 hover:text-white"><FileText className="w-4 h-4" /></button>
                  <button onClick={() => handlePreviewCampaign(c)} title="Preview email" data-testid={`rfq-preview-${c.id}`} className="p-1.5 text-white/60 hover:text-[#00D4FF]"><Eye className="w-4 h-4" /></button>
                  <button onClick={() => handleSend(c)} disabled={sendingId === c.id} title="Pre-Send Check & Send" data-testid={`rfq-send-${c.id}`} className="p-1.5 text-white/60 hover:text-[#00D4FF] disabled:opacity-50"><ShieldCheck className="w-4 h-4" /></button>
                  <button onClick={() => handleCopy(c)} title="Copy campaign" data-testid={`rfq-copy-${c.id}`} className="p-1.5 text-white/60 hover:text-white"><Copy className="w-4 h-4" /></button>
                  <button onClick={() => openEdit(c)} data-testid={`rfq-edit-${c.id}`} className="p-1.5 text-white/60 hover:text-white"><Pencil className="w-4 h-4" /></button>
                  <button onClick={() => handleDelete(c)} data-testid={`rfq-delete-${c.id}`} className="p-1.5 text-white/60 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Feb-2026 — 4-step RFQ Wizard replaces the legacy single-modal
          form. Reuses previewRfq, RfqPreSendPanel, and listSuppliers via
          the wizard component. */}
      <RfqWizard
        open={wizardOpen}
        editing={editing}
        onClose={() => setWizardOpen(false)}
        onSaved={() => { fetchData(); }}
      />

      {/* Send result modal */}
      {sendResult && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={() => setSendResult(null)}>
          <div onClick={(e) => e.stopPropagation()} className="w-full max-w-lg bg-[#0A0A0A] border border-white/10" data-testid="rfq-send-result">
            <div className="px-5 py-4 border-b border-white/5 flex items-center justify-between">
              <h3 className="text-lg font-semibold">Send Result — {sendResult.campaign.campaign_name}</h3>
              <button onClick={() => setSendResult(null)} className="text-white/40 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5">
              <div className="flex gap-4 mb-4 text-sm">
                <div className="flex items-center gap-2 text-emerald-300"><CheckCircle2 className="w-4 h-4" />Sent: {sendResult.sent}</div>
                <div className="flex items-center gap-2 text-red-300"><AlertCircle className="w-4 h-4" />Failed: {sendResult.failed}</div>
                <div className="text-white/40">Total: {sendResult.total}</div>
              </div>
              <div className="max-h-72 overflow-y-auto border border-white/10">
                {sendResult.results.map((r, i) => (
                  <div key={i} className="px-3 py-2 border-b border-white/5 text-xs flex justify-between">
                    <span>{r.company_name}</span>
                    <span className={r.status === 'sent' ? 'text-emerald-300' : 'text-red-300'}>{r.status}{r.error ? ` — ${r.error}` : ''}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Logs modal */}
      {logsModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={() => setLogsModal(null)}>
          <div onClick={(e) => e.stopPropagation()} className="w-full max-w-2xl bg-[#0A0A0A] border border-white/10 max-h-[80vh] overflow-y-auto" data-testid="rfq-logs-modal">
            <div className="px-5 py-4 border-b border-white/5 flex items-center justify-between sticky top-0 bg-[#0A0A0A]">
              <h3 className="text-lg font-semibold">Email Logs — {logsModal.campaign_name}</h3>
              <button onClick={() => setLogsModal(null)} className="text-white/40 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5">
              {logs.length === 0 ? (
                <div className="text-center text-white/40 py-8">No emails sent yet for this campaign.</div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="text-xs uppercase text-white/40">
                    <tr><th className="text-left py-2">Supplier</th><th className="text-left py-2">Email</th><th className="text-left py-2">Status</th><th className="text-left py-2">Sent</th></tr>
                  </thead>
                  <tbody>
                    {logs.map((l) => (
                      <tr key={l.id} className="border-t border-white/5">
                        <td className="py-2">{l.company_name}</td>
                        <td className="py-2 text-white/60 text-xs">{l.email || '—'}</td>
                        <td className="py-2"><span className={l.status === 'sent' ? 'text-emerald-300' : l.status === 'failed' ? 'text-red-300' : 'text-white/40'}>{l.status}</span>{l.error && <div className="text-xs text-red-300/70">{l.error}</div>}</td>
                        <td className="py-2 text-white/40 text-xs">{l.sent_at ? new Date(l.sent_at).toLocaleString() : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Email preview modal */}
      {previewData && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 p-4" onClick={() => setPreviewData(null)}>
          <div onClick={(e) => e.stopPropagation()} className="w-full max-w-2xl bg-[#0A0A0A] border border-[#00D4FF]/40 max-h-[90vh] overflow-y-auto" data-testid="rfq-preview-modal">
            <div className="flex items-center justify-between px-5 py-4 border-b border-white/5 sticky top-0 bg-[#0A0A0A]">
              <h3 className="text-lg font-semibold">Email preview — {previewData.campaign_name}</h3>
              <button onClick={() => setPreviewData(null)} className="text-white/40 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-4">
              {previewData.missing_product_request_text && (
                <div className="px-3 py-2 bg-red-400/15 border border-red-400/40 text-red-200 text-sm">
                  ⚠️ Product Request Text is empty but the body contains [PRODUCT_REQUEST_TEXT]. Fill it in before sending.
                </div>
              )}
              <div>
                <div className="text-xs uppercase tracking-wider text-white/40 mb-1">Subject</div>
                <div className="bg-white/5 border border-white/10 px-3 py-2 text-sm">{previewData.subject || '(empty)'}</div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-white/40 mb-1">Rendered body (what suppliers will receive)</div>
                <pre className="bg-white text-black px-4 py-3 text-sm whitespace-pre-wrap font-sans border border-white/10 max-h-[50vh] overflow-y-auto">{previewData.rendered_body}</pre>
              </div>
            </div>
            <div className="flex justify-end gap-3 px-5 py-4 border-t border-white/5 sticky bottom-0 bg-[#0A0A0A]">
              <button onClick={() => setPreviewData(null)} className="px-4 py-2 text-sm text-white/60 hover:text-white">Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Pre-Send Sanity Panel */}
      {preSendCampaign && (
        <RfqPreSendPanel
          campaign={preSendCampaign}
          onClose={() => setPreSendCampaign(null)}
          onSent={(result) => {
            setSendResult({ campaign: preSendCampaign, ...result });
            setPreSendCampaign(null);
            fetchData();
          }}
        />
      )}

      <style>{`
        .ipt{width:100%;background:rgba(255,255,255,0.05);border:1px solid rgba(255,255,255,0.1);padding:0.55rem 0.75rem;font-size:0.875rem;color:white;outline:none}
        .ipt:focus{border-color:rgba(0,212,255,0.5)}
        .ipt-select{
          width:100%;
          background:#0A0A0A;
          color:white;
          border:1px solid rgba(255,255,255,0.15);
          padding:0.55rem 0.75rem;
          font-size:0.875rem;
          outline:none;
          color-scheme: dark;
          appearance: none;
          -webkit-appearance: none;
          background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6' fill='none'%3e%3cpath d='M1 1L5 5L9 1' stroke='%2300D4FF' stroke-width='1.5' stroke-linecap='round'/%3e%3c/svg%3e");
          background-repeat: no-repeat;
          background-position: right 0.75rem center;
          padding-right: 2rem;
          cursor: pointer;
        }
        .ipt-select:focus{border-color:rgba(0,212,255,0.5)}
        .ipt-select:hover{border-color:rgba(0,212,255,0.3)}
        .ipt-select option{background:#0A0A0A;color:white;padding:8px}
        .ipt-select option:hover{background:#00D4FF;color:black}
        .ipt-select option:checked{background:#00D4FF;color:black;font-weight:600}
      `}</style>
    </div>
  );
};
